import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft, Instagram, Linkedin } from "lucide-react";
import BackButton from "@/components/BackButton";
import PageHeader from "@/components/PageHeader";
import { cleanPolicyText } from "@/utils/policyText";

const SECTIONS = [
  {
    n: "01",
    title: "Our Commitment",
    body:
      "At The Black House, we believe great design should be accessible to everyone. We are committed to providing a website that is inclusive and easy to use for people of all abilities. While building and improving theblackhouse.in, we strive to follow recognized accessibility best practices, including the Web Content Accessibility Guidelines (WCAG), wherever practical.",
  },
  {
    n: "02",
    title: "Inclusive Experience",
    body:
      "We aim to make our website usable with assistive technologies such as screen readers, keyboard navigation, voice controls, magnification software and other accessibility tools. Our objective is to create a simple, intuitive and enjoyable shopping experience for every visitor.",
  },
  {
    n: "03",
    title: "Our Ongoing Efforts",
    body:
      "Accessibility is an ongoing process. We regularly review our website, identify opportunities for improvement and implement updates that enhance usability. As our website grows, we will continue refining accessibility features and resolving issues whenever reasonably possible.",
  },
  {
    n: "04",
    title: "Third-Party Services",
    body:
      "Our website may integrate third-party services such as payment gateways, social media platforms, maps, embedded videos or analytics providers. While we encourage accessible experiences, we cannot guarantee the accessibility of third-party platforms or services that are outside our control.",
  },
  {
    n: "05",
    title: "Need Assistance?",
    body:
      "If you experience difficulty accessing any part of our website, require assistance placing an order or would like this statement in another format, please contact us. We value your feedback and will make reasonable efforts to assist you.",
  },
  {
    n: "06",
    title: "Continuous Improvement",
    body:
      "Accessibility is part of our commitment to delivering a better experience for every customer. We welcome suggestions that help us improve the usability of our website and services.",
  },
];

export default function Accessibility() {
  useEffect(() => {
    document.title = "Accessibility — THE BLACK HOUSE";
    window.scrollTo(0, 0);
  }, []);

  const ease = [0.16, 1, 0.3, 1];

  return (
    <main
      data-testid="accessibility-page"
      className="relative bg-black text-white overflow-x-hidden"
    >
      <PageHeader />
      <BackButton />
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 left-5 md:top-40 md:left-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase"
      >
        Legal · Accessibility
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 0.3 }}
        className="absolute top-36 right-5 md:top-40 md:right-10 z-10 font-mono-ui text-[9px] md:text-[10px] tracking-[0.35em] text-[#5a5a5a] uppercase text-right"
      >
        Last Updated · 18 · VII · MMXXVI
      </motion.div>

      <header className="max-w-4xl mx-auto px-6 md:px-10 pt-40 md:pt-44 pb-8 md:pb-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.1, ease }}
          className="flex flex-col items-center text-center"
        >
          <p className="font-mono-ui text-[10px] tracking-[0.4em] uppercase text-[#6a6a6a] mt-0">
            — Accessibility Statement
          </p>
          <h1
            data-testid="accessibility-title"
            className="font-display font-bold text-white uppercase mt-4 text-[10vw] sm:text-[6vw] md:text-[3.6vw] leading-[0.95]"
            style={{ letterSpacing: "0.18em" }}
          >
            A House For Everyone
          </h1>
          <div className="w-16 h-px bg-white/60 mt-8" />
          <p className="font-serif-editorial italic text-[#c9c9c4] max-w-lg mt-6 leading-[1.7] text-[15px] md:text-[17px]">
            {cleanPolicyText("Great design opens the door for every visitor. Here is how we work to keep ours open — and how to reach us if we can do better.")}
          </p>
        </motion.div>
      </header>

      <section className="max-w-4xl mx-auto px-6 md:px-10 pb-0">
        <div className="border-t border-[#141414]">
          {SECTIONS.map((s, i) => (
            <motion.article
              key={s.n}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-8%" }}
              transition={{ duration: 0.9, delay: i * 0.03, ease }}
              className="grid grid-cols-12 gap-4 md:gap-8 py-7 md:py-10 border-b border-[#141414]"
              data-testid={`accessibility-section-${s.n}`}
            >
              <div className="col-span-12 md:col-span-2">
                <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
                  § {s.n}
                </span>
              </div>
              <div className="col-span-12 md:col-span-10">
                <h2
                  className="font-display font-bold uppercase text-white text-[18px] md:text-[22px] leading-[1.2]"
                  style={{ letterSpacing: "0.16em" }}
                >
                  {s.title}
                </h2>
                <p className="font-serif-editorial text-[#c9c9c4] mt-4 leading-[1.85] text-[15px] md:text-[17px] max-w-2xl">
                  {cleanPolicyText(s.body)}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        {/* Contact block */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-8%" }}
          transition={{ duration: 0.9, ease }}
          className="grid grid-cols-12 gap-4 md:gap-8 py-6 md:py-8"
        >
          <div className="col-span-12 md:col-span-2">
            <span className="font-mono-ui text-[11px] md:text-[12px] tracking-[0.3em] text-[#7a7a7a]">
              Contact
            </span>
          </div>
          <div className="col-span-12 md:col-span-10 space-y-3 font-mono-ui text-[11px] md:text-[12px] tracking-[0.25em] uppercase text-[#c9c9c4]">
            <p>
              Website:{" "}
              <a
                href="https://www.theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                theblackhouse.in
              </a>
            </p>
            <p>
              Email:{" "}
              <a
                href="mailto:support@theblackhouse.in"
                className="text-white hover:underline underline-offset-4"
              >
                support@theblackhouse.in
              </a>
            </p>
            <p>Business Location: Faridabad, Haryana, India</p>
          </div>
        </motion.div>
      </section>

      <footer className="border-t border-[#141414]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-4 md:py-5 flex flex-col md:flex-row items-center justify-between gap-6">
          <Link
            data-testid="accessibility-back-home"
            to="/"
            className="group inline-flex items-center gap-2 font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
          >
            <ArrowLeft
              size={13}
              strokeWidth={1.4}
              className="transition-transform duration-500 group-hover:-translate-x-1"
            />
            Return To The House
          </Link>
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <Link
              to="/terms"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Terms
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/privacy"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Privacy
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <Link
              to="/security"
              className="font-mono-ui text-[10px] md:text-[11px] tracking-[0.3em] uppercase text-[#8a8a8a] hover:text-white transition-colors duration-500"
            >
              Security
            </Link>
            <span className="text-[#2a2a2a]">·</span>
            <a
              href="https://www.instagram.com/theblackhouse.tbh"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="Instagram"
              className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500"
            >
              <Instagram
                size={13}
                strokeWidth={1.4}
                className="text-white/70 group-hover:text-white transition-colors duration-500"
              />
            </a>
            <a
              href="https://www.linkedin.com/company/the-black-house/"
              target="_blank"
              rel="noreferrer noopener"
              aria-label="LinkedIn"
              className="group w-9 h-9 flex items-center justify-center border border-[#1a1a1a] hover:border-white/70 transition-colors duration-500"
            >
              <Linkedin
                size={13}
                strokeWidth={1.4}
                className="text-white/70 group-hover:text-white transition-colors duration-500"
              />
            </a>
          </div>
        </div>
        <div className="border-t border-[#141414] px-6 md:px-10 py-3 flex items-center justify-between font-mono-ui text-[8px] md:text-[9px] tracking-[0.3em] uppercase text-[#3a3a3a]">
          <span>© MMXXVI — THE BLACK HOUSE</span>
          <span>All rights are gathered.</span>
        </div>
      </footer>
    </main>
  );
}
